import * as React from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { cn } from "@/lib/utils";
import type { DashboardPeriod, RevenueChartPoint } from "./types";
import { formatCurrencyCompact, formatCurrencyMinor, formatDateShort } from "./utils";
import { DashboardPeriodSwitcher } from "./DashboardPeriodSwitcher";

export interface DashboardRevenueChartProps {
  series: RevenueChartPoint[];
  title: string;
  subtitle: string;
  period: DashboardPeriod;
  onPeriodChange: (period: DashboardPeriod) => void;
  periodLabels: Record<DashboardPeriod, string>;
  emptyLabel?: string;
  className?: string;
}

interface TooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; payload: RevenueChartPoint }>;
  label?: string;
}

function ChartTooltip({ active, payload }: TooltipProps) {
  if (!active || !payload?.length) return null;
  const point = payload[0];
  return (
    <div className="rounded-biz-card border border-biz-border bg-biz-surface-elevated px-3 py-2 biz-shadow-md">
      <div className="text-[11px] font-medium uppercase tracking-wide text-biz-foreground-muted">
        {formatDateShort(point.payload.date)}
      </div>
      <div className="mt-0.5 font-mono-num text-sm font-semibold text-biz-foreground">
        {formatCurrencyMinor(point.value)}
      </div>
    </div>
  );
}

export function DashboardRevenueChart({
  series,
  title,
  subtitle,
  period,
  onPeriodChange,
  periodLabels,
  emptyLabel,
  className,
}: DashboardRevenueChartProps) {
  const data = React.useMemo(
    () => series.map((p) => ({ ...p, valueMajor: p.valueMinor / 100 })),
    [series],
  );

  return (
    <section
      className={cn(
        "rounded-biz-card border border-biz-border bg-biz-surface-elevated p-4 biz-shadow-sm",
        className,
      )}
      aria-labelledby="dash-revenue-title"
    >
      <header className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h3 id="dash-revenue-title" className="text-base font-semibold text-biz-foreground">
            {title}
          </h3>
          <p className="text-xs text-biz-foreground-muted">{subtitle}</p>
        </div>
        <DashboardPeriodSwitcher
          period={period}
          onPeriodChange={onPeriodChange}
          labels={periodLabels}
          size="sm"
        />
      </header>
      <div className="mt-3 h-[240px] w-full">
        {data.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-biz-foreground-muted">
            {emptyLabel ?? "—"}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
              <defs>
                <linearGradient id="dashRevenueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--biz-primary))" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="hsl(var(--biz-primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="hsl(var(--biz-border))" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={(v) => formatDateShort(v)}
                tick={{ fill: "hsl(var(--biz-foreground-muted))", fontSize: 11 }}
                axisLine={{ stroke: "hsl(var(--biz-border))" }}
                tickLine={false}
                minTickGap={24}
              />
              <YAxis
                tickFormatter={(v) => formatCurrencyCompact(v * 100)}
                tick={{ fill: "hsl(var(--biz-foreground-muted))", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={56}
              />
              <Tooltip content={<ChartTooltip />} cursor={{ stroke: "hsl(var(--biz-border-strong))", strokeWidth: 1 }} />
              <Area
                type="monotone"
                dataKey="valueMajor"
                stroke="hsl(var(--biz-primary))"
                strokeWidth={2}
                fill="url(#dashRevenueGradient)"
                isAnimationActive
                animationDuration={220}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </section>
  );
}
